import { Component, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy, DestroyRef, inject, ElementRef, Output, EventEmitter, CUSTOM_ELEMENTS_SCHEMA, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom, Subscription } from 'rxjs';

import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { UrlStateService } from 'src/shared/services/url-state.service';
import { Util } from 'src/shared/util/util';
import { AddComplianceItemPage } from 'src/pages/compliance/add-compliance-item/add-compliance-item';
import { ComplianceItemDetailPage } from 'src/pages/compliance/compliance-item-detail/compliance-item-detail';

import { ComplianceApiService } from 'src/shared/services/compliance-api.service';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { DemoDataService } from 'src/shared/services/demo-data.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import {
  ComplianceCheckStatus,
  ComplianceBucket,
  ComplianceItem,
  ComplianceRentalType,
  ComplianceReport,
  ComplianceReportSection,
  ComplianceStatus,
} from 'src/models/compliance.model';
import {
  FilteredComplianceReport,
  filterReportForRental,
} from 'src/pages/compliance/util/compliance-report-filter.util';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

const SECTION_FALLBACK_TITLES: Record<string, string> = {
  blockers: 'Blockers',
  before_listing: 'Before Listing',
  operate: 'Operate',
  taxes: 'Taxes',
  admin: 'Admin',
  recommended: 'Recommended',
};

const SECTION_ORDER: ComplianceBucket[] = [
  'blockers',
  'before_listing',
  'operate',
  'taxes',
  'admin',
  'recommended',
];

// Buckets that are tucked away behind the "show more" toggle by default.
const HIDDEN_BY_DEFAULT_BUCKETS: ComplianceBucket[] = ['admin', 'recommended'];

const ELIGIBILITY_COLORS: Record<string, string> = {
  allowed: 'var(--tidy-success)',
  prohibited: 'var(--tidy-error)',
  conditional: 'var(--tidy-warning)',
  unknown: 'var(--tidy-text-muted)',
};

const ELIGIBILITY_BACKGROUNDS: Record<string, string> = {
  allowed: 'var(--tidy-success-bg)',
  prohibited: 'var(--tidy-error-bg)',
  conditional: 'var(--tidy-warning-bg)',
  unknown: 'var(--tidy-bg-hover)',
};

const ELIGIBILITY_LABELS: Record<string, string> = {
  allowed: 'Allowed',
  prohibited: 'Prohibited',
  conditional: 'Conditional',
  unknown: 'Unknown',
};

const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'var(--tidy-success)',
  medium: 'var(--tidy-warning)',
  low: 'var(--tidy-error)',
};

const STATUS_COLORS: Record<string, string> = {
  not_started: 'var(--tidy-text-muted)',
  in_progress: 'var(--tidy-warning)',
  completed: 'var(--tidy-success)',
  overdue: 'var(--tidy-error)',
};

const STATUS_BACKGROUNDS: Record<string, string> = {
  not_started: 'var(--tidy-bg-hover)',
  in_progress: 'var(--tidy-warning-bg)',
  completed: 'var(--tidy-success-bg)',
  overdue: 'var(--tidy-error-bg)',
};

const STATUS_LABELS: Record<string, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  overdue: 'Overdue',
};

const SECTION_ICONS: Record<string, string> = {
  blockers: 'assets/svg/alert-circle-outline.svg',
  before_listing: 'assets/svg/clipboard-outline.svg',
  operate: 'assets/svg/shield-checkmark-outline.svg',
  taxes: 'assets/svg/cash-outline.svg',
  admin: 'assets/svg/document-text-outline.svg',
  recommended: 'assets/svg/star-outline.svg',
};

const SECTION_ACCENTS: Record<string, string> = {
  blockers: 'var(--tidy-error-bg)',
  before_listing: 'var(--tidy-accent-light)',
  operate: 'var(--tidy-success-bg)',
  taxes: 'var(--tidy-warning-bg)',
  admin: 'var(--tidy-bg-hover)',
  recommended: 'var(--tidy-bg-hover)',
};

const RENTAL_TYPE_OPTIONS = [
  { value: 'STR' as ComplianceRentalType, label: 'Short Term Rental' },
  { value: 'LTR' as ComplianceRentalType, label: 'Long Term Rental' },
];

@Component({
  selector: 'compliance-content',
  templateUrl: './compliance-content.component.html',
  styleUrls: ['./compliance-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ComplianceContentComponent implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private complianceApi = inject(ComplianceApiService);
  private destroyRef = inject(DestroyRef);
  private client = inject(Client);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private util = inject(Util);
  private aboutThisPageService = inject(AboutThisPageService);
  private urlStateService = inject(UrlStateService);
  private demoDataService = inject(DemoDataService);
  private demoBlock = inject(DemoBlockService);

  readonly windowService = inject(WindowService);
  readonly rentalTypeOptions = RENTAL_TYPE_OPTIONS;

  rentalForm: UntypedFormGroup;
  loaded = false;
  errorMessage: string;
  addressResponse: any;
  addressItems: any[] = [];
  selectedAddressId: number | null = null;
  selectedPropertyValue: string | number = null;
  selectedRentalType: ComplianceRentalType = 'STR';
  complianceCheckStatus: ComplianceCheckStatus = null;
  isRunningCheck = false;
  report: ComplianceReport | null = null;
  filteredReport: FilteredComplianceReport = { eligibility: null, sections: [] };
  expandedSections: Record<string, boolean> = {};
  showHiddenSections = false;
  visibleSections: ComplianceReportSection[] = [];
  hiddenSections: ComplianceReportSection[] = [];
  hasAnyItems = false;
  searchQuery = '';
  statusFilter: ComplianceStatus | 'all' = 'all';
  bulkSavingSectionId: string | null = null;
  readonly statusFilterItems = [
    { viewValue: 'All', value: 'all' },
    { viewValue: 'Not Started', value: 'not_started' },
    { viewValue: 'In Progress', value: 'in_progress' },
    { viewValue: 'Completed', value: 'completed' },
    { viewValue: 'Overdue', value: 'overdue' },
  ];
  noCheckMessage = 'No compliance check has been run for this property.';
  private pollingInterval: any = null;
  private readonly POLLING_INTERVAL_MS = 12000;

  checkStartedAt: number | null = null;
  elapsedSeconds = 0;
  private elapsedInterval: any = null;
  readonly loadingSteps: { label: string; minSeconds: number }[] = [
    { label: 'Searching local regulations', minSeconds: 0 },
    { label: 'Identifying compliance requirements', minSeconds: 30 },
    { label: 'Evaluating eligibility for your rental type', minSeconds: 90 },
    { label: 'Building your action plan', minSeconds: 180 },
  ];

  readonly statusColors = STATUS_COLORS;
  readonly statusLabels = STATUS_LABELS;

  isSavingStatus = false;
  @Output() savingStatusChange = new EventEmitter<boolean>();

  showAboutThisPage = false;
  isDemo = false;
  overlayTop: string;
  private _complianceHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  @ViewChild('complianceHeader', { read: ElementRef }) set complianceHeader(el: ElementRef) {
    this._complianceHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }
  private panelClosedSubscription: Subscription;

  constructor() {
    this.rentalForm = this.fb.group({
      rentalType: ['STR' as ComplianceRentalType],
    });

    this.rentalForm.get('rentalType')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: ComplianceRentalType) => {
        if (!value || value === this.selectedRentalType) { return; }
        this.selectedRentalType = value;
        this.urlStateService.updateQueryParams({ rentalType: value });
        this.recomputeFilteredReport();
        this.cdr.markForCheck();
      });

    this.destroyRef.onDestroy(() => {
      this.stopPolling();
      this.stopElapsedTimer();
    });
  }

  async ngOnInit() {
    this.restoreRentalTypeFromUrl();

    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('compliance');
    this.isDemo = this.demoDataService.isDemoAccount();
    if (this.showAboutThisPage || this.isDemo) {
      this.loadMockData();
      return;
    }
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(this.errorMessage, 10000);
    }
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
  }

  private restoreRentalTypeFromUrl() {
    const fromUrl = this.urlStateService.getQueryParam('rentalType');
    if (fromUrl === 'STR' || fromUrl === 'LTR') {
      this.selectedRentalType = fromUrl;
      this.rentalForm.get('rentalType')?.setValue(fromUrl, { emitEvent: false });
    }
  }

  private loadMockData() {
    this.selectedAddressId = 1;
    this.complianceCheckStatus = 'completed';
    this.addressItems = [
      { value: 1, viewValue: '123 Main St' },
    ];
    this.selectedPropertyValue = 1;
    this.addressResponse = [{ id: 1, address_name: '123 Main St', address1: '123 Main St', color: '#4CAF50' }];

    const mockReport: ComplianceReport = {
      compliance_check_id: 1,
      status: 'completed',
      generated_at: new Date().toISOString(),
      raw_item_count: 6,
      visible_item_count: 5,
      eligibility: [
        {
          rental_type: 'STR',
          status: 'conditional',
          title: 'Short-term rentals are allowed with a permit',
          summary: 'You must obtain a short-term rental permit before listing.',
          confidence: 'high',
          source_items: [],
        },
        {
          rental_type: 'LTR',
          status: 'allowed',
          title: 'Long-term rentals are allowed',
          summary: 'No special permit is required for long-term rentals at this address.',
          confidence: 'high',
          source_items: [],
        },
      ],
      sections: [
        {
          id: 'before_listing',
          title: 'Before Listing',
          description: 'Complete these before accepting your first guest.',
          default_open: true,
          hidden_by_default: false,
          items: [
            this.makeMockItem(101, 'before_listing', 'STR', 'Apply for short-term rental permit at city hall.'),
            this.makeMockItem(102, 'before_listing', 'both', 'Register for transient occupancy tax (TOT).'),
          ],
        },
        {
          id: 'operate',
          title: 'Operate',
          description: 'Recurring obligations once you are live.',
          default_open: true,
          hidden_by_default: false,
          items: [
            this.makeMockItem(103, 'operate', 'both', 'Test smoke & CO detectors quarterly.'),
            this.makeMockItem(104, 'operate', 'STR', 'Renew rental permit annually.'),
          ],
        },
        {
          id: 'admin',
          title: 'Admin',
          description: 'Routine administrative items.',
          default_open: false,
          hidden_by_default: true,
          items: [
            this.makeMockItem(105, 'admin', 'both', 'Keep guest log on file for 2 years.'),
          ],
        },
      ],
    };

    this.report = mockReport;
    this.recomputeFilteredReport();
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private makeMockItem(
    id: number,
    bucket: ComplianceReportSection['id'],
    appliesTo: 'STR' | 'LTR' | 'both',
    description: string,
  ): ComplianceItem {
    return {
      id,
      address_id: 1,
      compliance_check_id: 1,
      bucket,
      what_to_do: description,
      last_completed_at: null,
      next_due_at: null,
      status: 'not_started',
      jurisdiction: 'City of Palm Desert',
      confidence: 'high',
      source_url: null,
      source_name: 'Palm Desert Municipal Code',
      citation: null,
      notes: null,
      is_tax: bucket === 'taxes',
      tax_rate_cents: null,
      platform_collected: false,
      applies_to: appliesTo,
      suppressed_for_rental_types: [],
      hidden_by_default: false,
      importance: 'required',
      source_items: [],
      created_at: '',
      updated_at: '',
    };
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._complianceHeader) { return; }
      const top = this._complianceHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top}px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.aboutThisPageService.dismissAboutThisPage('compliance');
    this.report = null;
    this.filteredReport = { eligibility: null, sections: [] };
    this.cdr.markForCheck();
  }

  async loadPage() {
    this.addressResponse = await this.client.getAddresses();
    this.addressItems = this.client.parseAddressList(this.addressResponse, true);
    this.addressItems.pop();

    if (!this.selectedPropertyValue && this.addressItems.length) {
      const savedId = localStorage.getItem('complianceAddressId') || localStorage.getItem('dashboardAddressId');
      const savedNum = savedId ? Number(savedId) : null;
      const match = savedNum && this.addressItems.find(a => a.value === savedNum);
      this.selectedPropertyValue = match ? savedNum : this.addressItems[0].value;
    }

    const selectedProperty = this.selectedPropertyValue;
    if (selectedProperty) {
      this.selectedAddressId = Number(selectedProperty);
      localStorage.setItem('complianceAddressId', String(this.selectedAddressId));
      await this.loadCheckStatus();

      if (this.complianceCheckStatus === 'pending' || this.complianceCheckStatus === 'processing') {
        this.startPolling();
      }

      await this.loadComplianceReport();
    } else {
      this.selectedAddressId = null;
      this.complianceCheckStatus = null;
      this.report = null;
    }

    this.recomputeFilteredReport();
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private async loadCheckStatus() {
    if (!this.selectedAddressId) { return; }
    try {
      const check = await firstValueFrom(this.complianceApi.getCheckStatus(this.selectedAddressId));
      this.complianceCheckStatus = check?.status || null;
      if ((this.complianceCheckStatus === 'pending' || this.complianceCheckStatus === 'processing') && check?.created_at) {
        this.startElapsedTimer(new Date(check.created_at).getTime());
      } else {
        this.stopElapsedTimer();
      }
    } catch {
      this.complianceCheckStatus = null;
    }
  }

  async loadComplianceReport() {
    if (!this.selectedAddressId) { return; }
    try {
      this.report = await firstValueFrom(this.complianceApi.getReport(this.selectedAddressId));
      await this.mergeManualItemsIntoReport();
    } catch {
      this.report = null;
    }
  }

  // The report is built from the AI displayPlan, which does not include items a
  // customer adds manually. Fetch the raw item list and fold any item that
  // isn't already in the report into its bucket section so additions show up.
  private async mergeManualItemsIntoReport() {
    if (!this.report || !this.selectedAddressId) { return; }
    let items: ComplianceItem[];
    try {
      items = await firstValueFrom(this.complianceApi.getItems(this.selectedAddressId));
    } catch {
      return;
    }

    const seenIds = new Set<number>();
    this.report.sections.forEach((section) => {
      (section.items ?? []).forEach((item) => seenIds.add(item.id));
    });

    // Only fold in manually-added items (no compliance_check_id). AI pipeline
    // items absent from the report were suppressed on purpose — don't resurface.
    const missing = items.filter(
      (item) => !seenIds.has(item.id) && item.compliance_check_id === null,
    );
    if (!missing.length) { return; }

    const sectionsById = new Map<ComplianceBucket, ComplianceReportSection>();
    this.report.sections.forEach((section) => sectionsById.set(section.id, section));

    missing.forEach((item) => {
      const bucket = (item.bucket || 'operate') as ComplianceBucket;
      let section = sectionsById.get(bucket);
      if (!section) {
        section = {
          id: bucket,
          title: SECTION_FALLBACK_TITLES[bucket] || bucket,
          description: null,
          default_open: true,
          hidden_by_default: HIDDEN_BY_DEFAULT_BUCKETS.includes(bucket),
          items: [],
        };
        sectionsById.set(bucket, section);
        this.report.sections.push(section);
      }
      section.items.push(item);
    });

    // Keep sections in the canonical bucket order.
    this.report.sections.sort(
      (a, b) => SECTION_ORDER.indexOf(a.id) - SECTION_ORDER.indexOf(b.id),
    );
    this.report.visible_item_count = this.report.sections.reduce(
      (sum, s) => sum + (s.items?.length ?? 0),
      0,
    );
  }

  private recomputeFilteredReport() {
    if (!this.report) {
      this.filteredReport = { eligibility: null, sections: [] };
      this.visibleSections = [];
      this.hiddenSections = [];
      this.hasAnyItems = false;
      return;
    }
    this.filteredReport = filterReportForRental(this.report, this.selectedRentalType);

    // Taxes now live on their own Finance → Taxes tab. Drop the taxes section
    // from the Compliance page (a callout links there instead), so counts,
    // filters, and the empty state all exclude it.
    this.filteredReport.sections = this.filteredReport.sections.filter((section) => section.id !== 'taxes');

    // Completed items sink to the bottom of each section.
    this.filteredReport.sections.forEach((section) => {
      section.items.sort((a, b) => {
        const aDone = a.status === 'completed' ? 1 : 0;
        const bDone = b.status === 'completed' ? 1 : 0;
        return aDone - bDone;
      });
    });

    // Apply search + status filters on top of the rental-type slice.
    const hasActiveFilters = !!this.searchQuery.trim() || this.statusFilter !== 'all';
    const query = this.searchQuery.trim().toLowerCase();
    const filteredSections = this.filteredReport.sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => this.itemMatchesFilters(item, query)),
      }))
      .filter((section) => section.items.length > 0);

    const persisted = this.loadPersistedExpandedSections();
    const next: Record<string, boolean> = {};
    filteredSections.forEach((section) => {
      if (hasActiveFilters) {
        // Auto-expand sections that match the active filters so results are visible.
        next[section.id] = true;
      } else if (persisted && Object.prototype.hasOwnProperty.call(persisted, section.id)) {
        next[section.id] = persisted[section.id];
      } else {
        const prev = this.expandedSections[section.id];
        next[section.id] = prev !== undefined ? prev : section.default_open;
      }
    });
    this.expandedSections = next;

    if (hasActiveFilters) {
      // When filtering, treat all matching sections as visible so the user
      // doesn't have to expand "Recommendations & admin" to find a match.
      this.visibleSections = filteredSections;
      this.hiddenSections = [];
    } else {
      this.visibleSections = filteredSections.filter((s) => !s.hidden_by_default);
      this.hiddenSections = filteredSections.filter((s) => s.hidden_by_default);
    }
    this.hasAnyItems = this.filteredReport.sections.some((s) => s.items.length > 0);
  }

  private itemMatchesFilters(item: ComplianceItem, normalizedQuery: string): boolean {
    if (this.statusFilter !== 'all' && item.status !== this.statusFilter) {
      return false;
    }
    if (!normalizedQuery) { return true; }
    const haystack = [
      item.what_to_do,
      item.name,
      item.jurisdiction,
      item.source_name,
      item.how_to_do_it,
      item.notes,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(normalizedQuery);
  }

  onSearchChange(query: string) {
    this.searchQuery = query || '';
    this.recomputeFilteredReport();
    this.cdr.markForCheck();
  }

  onStatusFilterChange(value: ComplianceStatus | 'all') {
    this.statusFilter = value;
    this.recomputeFilteredReport();
    this.cdr.markForCheck();
  }

  clearFilters() {
    if (!this.searchQuery && this.statusFilter === 'all') { return; }
    this.searchQuery = '';
    this.statusFilter = 'all';
    this.recomputeFilteredReport();
    this.cdr.markForCheck();
  }

  get hasActiveFilters(): boolean {
    return !!this.searchQuery.trim() || this.statusFilter !== 'all';
  }

  /** Name of the active tab, echoed as the second desktop header's title. */
  get selectedRentalTypeLabel(): string {
    return this.rentalTypeOptions.find((option) => option.value === this.selectedRentalType)?.label || '';
  }

  /** The plan view is up, so the tabs and the search/status filter apply. */
  get isReportVisible(): boolean {
    return this.loaded
      && !!this.selectedAddressId
      && !!this.report
      && this.complianceCheckStatus !== 'pending'
      && this.complianceCheckStatus !== 'processing';
  }

  get showFilters(): boolean {
    return this.isReportVisible && (this.hasAnyItems || this.hasActiveFilters);
  }

  async markAllInSectionComplete(section: ComplianceReportSection) {
    if (this.showAboutThisPage || this.isDemo) { return; }
    if (this.demoBlock.block()) { return; }
    const targets = section.items.filter((i) => i.status !== 'completed');
    if (!targets.length) { return; }

    const alert = await this.util.showConfirmAlert({
      message: `Mark all ${targets.length} ${section.title || section.id} items as complete?`,
    });
    const { role } = await alert.onDidDismiss();
    if (role !== 'confirm') { return; }

    this.bulkSavingSectionId = section.id;
    this.isSavingStatus = true;
    this.savingStatusChange.emit(true);
    this.cdr.markForCheck();

    const previousStatuses = new Map<number, ComplianceStatus>();
    targets.forEach((i) => {
      previousStatuses.set(i.id, i.status);
      i.status = 'completed';
    });

    try {
      await Promise.all(
        targets.map((i) =>
          firstValueFrom(
            this.complianceApi.customerUpdateItem(i.id, { params: { status: 'completed' } }),
          ),
        ),
      );
      this.recomputeFilteredReport();
    } catch {
      targets.forEach((i) => {
        const prev = previousStatuses.get(i.id);
        if (prev) { i.status = prev; }
      });
      this.util.showError('Failed to mark all items complete. Please try again.');
    } finally {
      this.bulkSavingSectionId = null;
      this.isSavingStatus = false;
      this.savingStatusChange.emit(false);
      this.cdr.markForCheck();
    }
  }

  sectionHasIncomplete(section: ComplianceReportSection): boolean {
    return section.items.some((i) => i.status !== 'completed');
  }

  async onDesktopPropertyChange() {
    this.loaded = false;
    this.stopPolling();
    if (this.selectedPropertyValue) {
      localStorage.setItem('complianceAddressId', String(this.selectedPropertyValue));
    }
    this.cdr.markForCheck();
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message;
      this.loaded = true;
      this.cdr.markForCheck();
    }
  }

  toggleSection(sectionId: string) {
    this.expandedSections = {
      ...this.expandedSections,
      [sectionId]: !this.expandedSections[sectionId],
    };
    this.persistExpandedSections();
    this.cdr.markForCheck();
  }

  private get expandedSectionsStorageKey(): string | null {
    return this.selectedAddressId
      ? `compliance_expanded_sections_${this.selectedAddressId}`
      : null;
  }

  private loadPersistedExpandedSections(): Record<string, boolean> | null {
    const key = this.expandedSectionsStorageKey;
    if (!key) { return null; }
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private persistExpandedSections() {
    const key = this.expandedSectionsStorageKey;
    if (!key) { return; }
    try {
      localStorage.setItem(key, JSON.stringify(this.expandedSections));
    } catch {
      // Ignore quota errors; non-essential UI state.
    }
  }

  toggleHiddenSections() {
    this.showHiddenSections = !this.showHiddenSections;
    this.cdr.markForCheck();
  }

  async triggerComplianceCheck() {
    if (this.showAboutThisPage || this.isDemo) { return; }
    if (this.demoBlock.block()) { return; }
    if (!this.selectedAddressId) { return; }
    try {
      this.isRunningCheck = true;
      this.complianceCheckStatus = 'pending';
      this.startElapsedTimer(Date.now());
      this.cdr.markForCheck();
      await firstValueFrom(this.complianceApi.triggerCheck(this.selectedAddressId));
      this.startPolling();
    } catch {
      this.isRunningCheck = false;
      this.complianceCheckStatus = 'failed';
      this.stopElapsedTimer();
      this.cdr.markForCheck();
      this.util.showError('Failed to start compliance check. Please try again.', 5000);
    }
  }

  private startPolling() {
    this.stopPolling();
    this.pollingInterval = setInterval(async () => {
      await this.pollCheckStatus();
    }, this.POLLING_INTERVAL_MS);
  }

  private stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private startElapsedTimer(startedAtMs: number) {
    this.stopElapsedTimer();
    this.checkStartedAt = startedAtMs;
    this.elapsedSeconds = Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000));
    this.elapsedInterval = setInterval(() => {
      this.elapsedSeconds = Math.max(0, Math.floor((Date.now() - this.checkStartedAt) / 1000));
      this.cdr.markForCheck();
    }, 1000);
  }

  private stopElapsedTimer() {
    if (this.elapsedInterval) {
      clearInterval(this.elapsedInterval);
      this.elapsedInterval = null;
    }
    this.checkStartedAt = null;
    this.elapsedSeconds = 0;
  }

  get currentLoadingStepIndex(): number {
    let idx = 0;
    for (let i = 0; i < this.loadingSteps.length; i++) {
      if (this.elapsedSeconds >= this.loadingSteps[i].minSeconds) {
        idx = i;
      }
    }
    return idx;
  }

  get elapsedDisplay(): string {
    const m = Math.floor(this.elapsedSeconds / 60);
    const s = this.elapsedSeconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  private async pollCheckStatus() {
    if (!this.selectedAddressId) {
      this.stopPolling();
      return;
    }
    try {
      const check = await firstValueFrom(this.complianceApi.getCheckStatus(this.selectedAddressId));
      const status = check?.status || null;

      if (status === 'completed') {
        this.stopPolling();
        this.stopElapsedTimer();
        this.isRunningCheck = false;
        this.complianceCheckStatus = 'completed';
        await this.loadComplianceReport();
        this.recomputeFilteredReport();
        this.cdr.markForCheck();
      } else if (status === 'failed') {
        this.stopPolling();
        this.stopElapsedTimer();
        this.isRunningCheck = false;
        this.complianceCheckStatus = 'failed';
        this.cdr.markForCheck();
        this.util.showError('Compliance check failed. Please try again.', 5000);
      } else if ((status === 'pending' || status === 'processing') && !this.elapsedInterval && check?.created_at) {
        // Reload mid-check: anchor elapsed timer to the server-side check start.
        this.startElapsedTimer(new Date(check.created_at).getTime());
      }
    } catch {
      // Silently retry on next interval
    }
  }

  readonly eligibilityColors = ELIGIBILITY_COLORS;
  readonly eligibilityBackgrounds = ELIGIBILITY_BACKGROUNDS;
  readonly eligibilityLabels = ELIGIBILITY_LABELS;
  readonly confidenceColors = CONFIDENCE_COLORS;
  readonly statusBackgrounds = STATUS_BACKGROUNDS;

  goToAddComplianceItem() {
    if (this.showAboutThisPage || this.isDemo) { return; }
    if (this.demoBlock.block()) { return; }
    const params = { selectedAddressId: this.selectedAddressId };
    this.goToPage('add-compliance-item', params, AddComplianceItemPage);
  }

  goToCompletedCompliance() {
    if (this.showAboutThisPage || this.isDemo) { return; }
    this.navCtrl.navigateForward('completed-compliance', {
      selectedAddressId: this.selectedAddressId,
    });
  }

  goToItemDetail(itemId: number) {
    if (this.showAboutThisPage || this.isDemo) { return; }
    if (this.demoBlock.block()) { return; }
    const params = { complianceItemId: itemId };
    this.goToPage('compliance-item-detail', params, ComplianceItemDetailPage);
  }

  goToTaxes() {
    if (this.showAboutThisPage || this.isDemo) { return; }
    this.navCtrl.navigateForward('finance?tab=taxes');
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(async () => {
        await this.loadPage();
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  async quickToggleItemComplete(item: ComplianceItem) {
    if (this.showAboutThisPage || this.isDemo) { return; }
    if (this.demoBlock.block()) { return; }
    const newStatus: ComplianceStatus = item.status === 'completed' ? 'not_started' : 'completed';
    const previousStatus = item.status;

    this.isSavingStatus = true;
    this.savingStatusChange.emit(true);
    item.status = newStatus;
    this.cdr.markForCheck();

    try {
      await firstValueFrom(
        this.complianceApi.customerUpdateItem(item.id, { params: { status: newStatus } }),
      );
      if (newStatus === 'completed') {
        this.recomputeFilteredReport();
      }
    } catch {
      item.status = previousStatus;
      this.util.showError('Failed to update status. Please try again.');
    } finally {
      this.isSavingStatus = false;
      this.savingStatusChange.emit(false);
      this.cdr.markForCheck();
    }
  }

  readonly sectionFallbackTitles = SECTION_FALLBACK_TITLES;
  readonly sectionIcons = SECTION_ICONS;
  readonly sectionAccents = SECTION_ACCENTS;

  selectRentalType(value: string) {
    if (value === this.selectedRentalType) { return; }
    this.rentalForm.get('rentalType')?.setValue(value as ComplianceRentalType);
  }
}
